import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Plus, ChevronUp, ChevronDown, Sparkles } from "lucide-react";

type Article = {
  id: string;
  slug: string;
  title: string;
  body: string;
  tags: string[];
  status: "live" | "draft" | "archived";
  source: "curated" | "seed" | "ai_drafted";
  sourceChatId?: string | null;
  sortOrder: number;
  updatedAt: string;
};

type Chat = {
  id: string;
  workspaceId: string;
  userId: string;
  title: string | null;
  totalCostCents: number;
  lastFeedback: string | null;
  flaggedOffTopic: boolean;
  createdAt: string;
};

type ChatMessage = {
  id: string;
  role: string;
  content: string;
  pass: string | null;
  modelKey: string | null;
  costCents: number;
  createdAt: string;
};

type Settings = {
  perWorkspaceMonthlyCapCents: number;
  globalMonthlyCapCents: number;
  retentionDaysDefault: number;
  retentionDaysFlagged: number;
  helpAssistantEnabled: boolean;
  lastGlobalAlertMonth: string | null;
  autoPromoteAfterDays: number | null;
};


// ── Articles ─────────────────────────────────────────────────────

export function ArticlesTab() {
  const { toast } = useToast();
  const [editing, setEditing] = useState<Article | null>(null);
  const [creating, setCreating] = useState(false);
  const [showAiDraftedOnly, setShowAiDraftedOnly] = useState(false);
  const { data, isLoading } = useQuery<{ articles: Article[] }>({
    queryKey: ["/api/platform/help-kb/articles"],
  });

  const aiDraftedPending = (data?.articles || []).filter(
    (a) => a.source === "ai_drafted" && a.status !== "live" && a.status !== "archived",
  );
  const displayed = showAiDraftedOnly
    ? (data?.articles || []).filter((a) => a.source === "ai_drafted")
    : (data?.articles || []);

  const promote = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiRequest("POST", `/api/platform/help-kb/articles/${id}/promote`, {});
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/help-kb/articles"] });
      toast({ title: "Article promoted to live" });
    },
  });

  const archive = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiRequest("POST", `/api/platform/help-kb/articles/${id}/archive`, {});
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/help-kb/articles"] });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/platform/help-kb/articles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/help-kb/articles"] });
      toast({ title: "Article deleted" });
    },
  });

  const move = useMutation({
    mutationFn: async (args: { id: string; direction: "up" | "down" }) => {
      const r = await apiRequest("POST", `/api/platform/help-kb/articles/${args.id}/move`, { direction: args.direction });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/help-kb/articles"] });
    },
  });

  return (
    <div className="space-y-3">
      {aiDraftedPending.length > 0 && (
        <div
          className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-4 py-3 text-sm"
          data-testid="banner-ai-drafted-pending"
        >
          <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="flex-1 text-amber-800 dark:text-amber-300">
            <strong>{aiDraftedPending.length}</strong> AI-generated article{aiDraftedPending.length !== 1 ? "s" : ""} need review before going live.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900"
            onClick={() => setShowAiDraftedOnly(true)}
            data-testid="button-review-ai-drafted"
          >
            Review
          </Button>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {displayed.length} {showAiDraftedOnly ? "AI-drafted" : ""} article{displayed.length !== 1 ? "s" : ""}
          </span>
          {showAiDraftedOnly && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={() => setShowAiDraftedOnly(false)}
              data-testid="button-clear-ai-filter"
            >
              Clear filter
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showAiDraftedOnly ? "default" : "outline"}
            onClick={() => setShowAiDraftedOnly((v) => !v)}
            data-testid="button-filter-ai-drafted"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            AI-drafted
            {aiDraftedPending.length > 0 && (
              <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0 h-4">
                {aiDraftedPending.length}
              </Badge>
            )}
          </Button>
          <Button onClick={() => setCreating(true)} size="sm" data-testid="button-new-article">
            <Plus className="w-4 h-4 mr-1" /> New article
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {isLoading && <div className="text-sm">Loading…</div>}
        {displayed.map((a) => (
          <Card
            key={a.id}
            data-testid={`row-article-${a.id}`}
            className={a.source === "ai_drafted" && a.status === "draft" ? "border-amber-300 dark:border-amber-700" : ""}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-1">
                  {a.source === "ai_drafted" && (
                    <Sparkles className="w-3 h-3 text-amber-500 shrink-0" title="AI-generated — pending review" />
                  )}
                  {a.title}
                </CardTitle>
                <div className="flex gap-1">
                  {a.source === "ai_drafted" && a.status === "draft" ? (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                      pending review
                    </Badge>
                  ) : (
                    <Badge variant={a.status === "live" ? "default" : a.status === "draft" ? "secondary" : "outline"}>
                      {a.status}
                    </Badge>
                  )}
                  <Badge variant="outline">{a.source}</Badge>
                </div>
              </div>
              <div className="text-xs text-muted-foreground font-mono">{a.slug}</div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground line-clamp-3">{a.body}</p>
              <div className="flex flex-wrap gap-1">
                {a.tags.map((t) => (
                  <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                ))}
              </div>
              <div className="flex gap-1 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => move.mutate({ id: a.id, direction: "up" })}
                  data-testid={`button-move-up-${a.id}`}
                  title="Move up"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => move.mutate({ id: a.id, direction: "down" })}
                  data-testid={`button-move-down-${a.id}`}
                  title="Move down"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`button-edit-${a.id}`}>
                  Edit
                </Button>
                {a.status !== "live" && (
                  <Button size="sm" variant="outline" onClick={() => promote.mutate(a.id)} data-testid={`button-promote-${a.id}`}>
                    Promote
                  </Button>
                )}
                {a.status !== "archived" && (
                  <Button size="sm" variant="outline" onClick={() => archive.mutate(a.id)} data-testid={`button-archive-${a.id}`}>
                    Archive
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(a.id)} data-testid={`button-delete-${a.id}`}>
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {(editing || creating) && (
        <ArticleEditor
          article={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </div>
  );
}

function ArticleEditor({ article, onClose }: { article: Article | null; onClose: () => void }) {
  const { toast } = useToast();
  const [title, setTitle] = useState(article?.title || "");
  const [slug, setSlug] = useState(article?.slug || "");
  const [body, setBody] = useState(article?.body || "");
  const [tags, setTags] = useState((article?.tags || []).join(", "));
  const [status, setStatus] = useState<string>(article?.status || "live");

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        slug,
        body,
        status,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (article) {
        const r = await apiRequest("PATCH", `/api/platform/help-kb/articles/${article.id}`, payload);
        return r.json();
      } else {
        const r = await apiRequest("POST", "/api/platform/help-kb/articles", payload);
        return r.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/help-kb/articles"] });
      toast({ title: article ? "Article updated" : "Article created" });
      onClose();
    },
    onError: (e: any) => toast({ title: "Save failed", description: String(e?.message || e), variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{article ? "Edit article" : "New article"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="input-article-title" />
          </div>
          <div>
            <Label>Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="input-article-slug" />
          </div>
          <div>
            <Label>Body (markdown)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} data-testid="input-article-body" />
          </div>
          <div>
            <Label>Tags (comma-separated)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="input-article-tags" />
          </div>
          <div>
            <Label>Status</Label>
            <select
              className="border rounded px-2 py-1 text-sm w-full bg-background"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="select-article-status"
            >
              <option value="live">Live</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-article">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Chats ────────────────────────────────────────────────────────

export function ChatsTab() {
  const [openChat, setOpenChat] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string>("");
  const [flagged, setFlagged] = useState(false);
  const [hasClaude, setHasClaude] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] = useState<string>("");
  const url = `/api/platform/help-kb/chats?${new URLSearchParams({
    ...(feedback ? { feedback } : {}),
    ...(flagged ? { flagged: "1" } : {}),
    ...(hasClaude ? { hasClaude: "1" } : {}),
    ...(workspaceFilter.trim() ? { workspaceId: workspaceFilter.trim() } : {}),
  }).toString()}`;
  const { data, isLoading } = useQuery<{ chats: Chat[] }>({
    queryKey: [url],
  });
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <Input
          placeholder="Filter by workspace ID"
          value={workspaceFilter}
          onChange={(e) => setWorkspaceFilter(e.target.value)}
          className="w-64 text-sm"
          data-testid="input-workspace-filter"
        />
        <select
          className="border rounded px-2 py-1 text-sm bg-background"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          data-testid="select-feedback-filter"
        >
          <option value="">All feedback</option>
          <option value="up">Thumbs up</option>
          <option value="down">Thumbs down</option>
        </select>
        <label className="flex items-center gap-1 text-sm">
          <Switch checked={flagged} onCheckedChange={setFlagged} data-testid="switch-flagged-only" />
          Flagged only
        </label>
        <label className="flex items-center gap-1 text-sm">
          <Switch checked={hasClaude} onCheckedChange={setHasClaude} data-testid="switch-has-claude" />
          Escalated to Claude
        </label>
      </div>
      {isLoading && <div className="text-sm">Loading…</div>}
      <div className="space-y-2">
        {data?.chats?.map((c) => (
          <Card
            key={c.id}
            className="cursor-pointer"
            onClick={() => setOpenChat(c.id)}
            data-testid={`row-chat-${c.id}`}
          >
            <CardContent className="pt-3 pb-3 flex justify-between items-center gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.title || "(untitled)"}</div>
                <div className="text-xs text-muted-foreground">
                  ws {c.workspaceId.slice(0, 8)}… · ${(c.totalCostCents / 100).toFixed(2)} · {new Date(c.createdAt).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-1">
                {c.flaggedOffTopic && <Badge variant="destructive">off-topic</Badge>}
                {c.lastFeedback && (
                  <Badge variant={c.lastFeedback === "down" ? "destructive" : "default"}>
                    {c.lastFeedback === "down" ? "👎" : "👍"}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {openChat && <ChatDetail chatId={openChat} onClose={() => setOpenChat(null)} />}
    </div>
  );
}

function ChatDetail({ chatId, onClose }: { chatId: string; onClose: () => void }) {
  const { toast } = useToast();
  const [revealed, setRevealed] = useState(false);
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const url = revealed
    ? `/api/platform/help-kb/chats/${chatId}?reveal=1`
    : `/api/platform/help-kb/chats/${chatId}`;
  const { data } = useQuery<{ chat: Chat; messages: ChatMessage[]; revealed?: boolean }>({
    queryKey: [url],
  });
  const reveal = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", `/api/platform/help-kb/chats/${chatId}/reveal`, { password });
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "PII revealed (audit-logged, 5 min window)" });
      setRevealed(true);
      setShowPwd(false);
      setPassword("");
      queryClient.invalidateQueries({ queryKey: [`/api/platform/help-kb/chats/${chatId}?reveal=1`] });
    },
    onError: (e: any) => toast({ title: "Reveal failed", description: String(e?.message || e), variant: "destructive" }),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>Chat transcript {data?.revealed ? "(PII visible)" : "(PII redacted)"}</span>
            {!showPwd && !data?.revealed && (
              <Button size="sm" variant="outline" onClick={() => setShowPwd(true)} data-testid="button-reveal-pii">
                Reveal PII
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        {showPwd && !data?.revealed && (
          <div className="border rounded p-3 mb-3 space-y-2 bg-muted/30">
            <Label>Confirm your platform password to reveal PII (audit-logged)</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="input-reveal-password"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => reveal.mutate()} disabled={!password || reveal.isPending} data-testid="button-reveal-confirm">
                {reveal.isPending ? "Verifying…" : "Confirm reveal"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowPwd(false); setPassword(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        <div className="space-y-2">
          {data?.messages?.map((m) => (
            <div key={m.id} className="text-sm border-l-2 pl-2" data-testid={`message-${m.id}`}>
              <div className="text-xs text-muted-foreground">
                {m.role} {m.pass && `· ${m.pass}`} {m.modelKey && `· ${m.modelKey}`} {m.costCents > 0 && `· $${(m.costCents / 100).toFixed(4)}`}
              </div>
              <div className="whitespace-pre-wrap">{m.content}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Settings ─────────────────────────────────────────────────────

export function HelpSettingsTab() {
  const { toast } = useToast();
  const { data } = useQuery<{
    settings: Settings;
    globalSpendCents: number;
    perWorkspaceSpend: Array<{ workspaceId: string; cents: number }>;
  }>({
    queryKey: ["/api/platform/help-kb/settings"],
  });

  const [perWorkspaceDollars, setPerWorkspaceDollars] = useState<string>("");
  const [globalDollars, setGlobalDollars] = useState<string>("");
  const [retentionDefault, setRetentionDefault] = useState<string>("");
  const [retentionFlagged, setRetentionFlagged] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [autoPromoteEnabled, setAutoPromoteEnabled] = useState<boolean | null>(null);
  const [autoPromoteDays, setAutoPromoteDays] = useState<string>("");

  const effective = data?.settings;
  const pw = perWorkspaceDollars !== "" ? perWorkspaceDollars : effective ? (effective.perWorkspaceMonthlyCapCents / 100).toFixed(2) : "";
  const gl = globalDollars !== "" ? globalDollars : effective ? (effective.globalMonthlyCapCents / 100).toFixed(2) : "";
  const rd = retentionDefault !== "" ? retentionDefault : effective ? String(effective.retentionDaysDefault) : "";
  const rf = retentionFlagged !== "" ? retentionFlagged : effective ? String(effective.retentionDaysFlagged) : "";
  const en = enabled !== null ? enabled : effective?.helpAssistantEnabled ?? true;
  const apOn = autoPromoteEnabled !== null ? autoPromoteEnabled : (effective?.autoPromoteAfterDays != null);
  const apDays = autoPromoteDays !== "" ? autoPromoteDays : effective?.autoPromoteAfterDays != null ? String(effective.autoPromoteAfterDays) : "14";

  const save = useMutation({
    mutationFn: async () => {
      const payload: any = {
        perWorkspaceMonthlyCapCents: Math.round(Number(pw) * 100),
        globalMonthlyCapCents: Math.round(Number(gl) * 100),
        retentionDaysDefault: Number(rd),
        retentionDaysFlagged: Number(rf),
        helpAssistantEnabled: en,
        autoPromoteAfterDays: apOn ? Math.max(1, Math.min(365, Number(apDays) || 14)) : null,
      };
      const r = await apiRequest("PATCH", "/api/platform/help-kb/settings", payload);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/help-kb/settings"] });
      toast({ title: "Settings saved" });
    },
  });

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Global settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Per-workspace monthly cap ($)</Label>
              <Input value={pw} onChange={(e) => setPerWorkspaceDollars(e.target.value)} data-testid="input-cap-per-workspace" />
            </div>
            <div>
              <Label>Global monthly cap ($)</Label>
              <Input value={gl} onChange={(e) => setGlobalDollars(e.target.value)} data-testid="input-cap-global" />
            </div>
            <div>
              <Label>Retention (days, default)</Label>
              <Input value={rd} onChange={(e) => setRetentionDefault(e.target.value)} data-testid="input-retention-default" />
            </div>
            <div>
              <Label>Retention (days, flagged/rated)</Label>
              <Input value={rf} onChange={(e) => setRetentionFlagged(e.target.value)} data-testid="input-retention-flagged" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={en} onCheckedChange={(v) => setEnabled(v)} data-testid="switch-enabled" />
            Help assistant enabled platform-wide
          </label>
          <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Switch
                checked={apOn}
                onCheckedChange={(v) => {
                  setAutoPromoteEnabled(v);
                }}
                data-testid="switch-auto-promote"
              />
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Auto-promote AI-drafted articles
            </label>
            <p className="text-xs text-muted-foreground">
              When enabled, AI-generated draft articles that originated from a real chat gap and have not been
              reviewed within the threshold will be automatically promoted to live each night.
            </p>
            {apOn && (
              <div className="flex items-center gap-2">
                <Label className="text-sm shrink-0">Promote after</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  className="w-20 text-sm"
                  value={apDays}
                  onChange={(e) => setAutoPromoteDays(e.target.value)}
                  data-testid="input-auto-promote-days"
                />
                <span className="text-sm text-muted-foreground">days without review</span>
              </div>
            )}
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-settings">
            {save.isPending ? "Saving…" : "Save settings"}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Spend this month</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-sm">Global: <strong>${((data?.globalSpendCents || 0) / 100).toFixed(2)}</strong></div>
          <div className="text-xs text-muted-foreground">By workspace (top 20):</div>
          <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
            {(data?.perWorkspaceSpend || [])
              .sort((a, b) => b.cents - a.cents)
              .slice(0, 20)
              .map((r) => (
                <li key={r.workspaceId} className="font-mono">
                  {r.workspaceId.slice(0, 12)}… — ${(r.cents / 100).toFixed(2)}
                </li>
              ))}
          </ul>
        </CardContent>
      </Card>
      <WorkspaceOverridesCard />
    </div>
  );
}

// Per-workspace cap / disabled override editor. Loads on demand by
// workspace ID and saves via PATCH /api/platform/help-kb/overrides/:wsId.
function WorkspaceOverridesCard() {
  const { toast } = useToast();
  const [workspaceId, setWorkspaceId] = useState("");
  const [capDollars, setCapDollars] = useState("");
  const [disabled, setDisabled] = useState(false);

  const { data, refetch, isFetching } = useQuery<{
    override:
      | { workspaceId: string; monthlyCapCentsOverride: number | null; disabled: boolean }
      | null;
  }>({
    queryKey: ["/api/platform/help-kb/overrides", workspaceId],
    enabled: false,
  });

  const load = async () => {
    if (!workspaceId.trim()) return;
    const r = await refetch();
    const o = r.data?.override;
    setCapDollars(
      o?.monthlyCapCentsOverride != null
        ? (o.monthlyCapCentsOverride / 100).toFixed(2)
        : "",
    );
    setDisabled(!!o?.disabled);
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload: any = { disabled };
      if (capDollars.trim() !== "") {
        payload.monthlyCapCentsOverride = Math.round(Number(capDollars) * 100);
      } else {
        payload.monthlyCapCentsOverride = null;
      }
      const r = await apiRequest(
        "PATCH",
        `/api/platform/help-kb/overrides/${encodeURIComponent(workspaceId.trim())}`,
        payload,
      );
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Override saved" });
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/help-kb/overrides", workspaceId],
      });
    },
    onError: (e: any) =>
      toast({
        title: "Save failed",
        description: String(e?.message || e),
        variant: "destructive",
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Per-workspace overrides</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Workspace ID</Label>
            <Input
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              placeholder="e.g. ec98589d-..."
              data-testid="input-override-workspace-id"
            />
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={!workspaceId.trim() || isFetching}
            data-testid="button-load-override"
          >
            {isFetching ? "Loading…" : "Load"}
          </Button>
        </div>
        {data && (
          <div className="space-y-3 border-t pt-3">
            <div className="text-xs text-muted-foreground">
              {data.override
                ? "Current override loaded."
                : "No override exists yet — saving will create one."}
            </div>
            <div>
              <Label>Monthly cap override ($, leave blank to inherit global)</Label>
              <Input
                value={capDollars}
                onChange={(e) => setCapDollars(e.target.value)}
                placeholder="e.g. 25.00"
                data-testid="input-override-cap"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={disabled}
                onCheckedChange={(v) => setDisabled(v)}
                data-testid="switch-override-disabled"
              />
              Disable help assistant for this workspace
            </label>
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              data-testid="button-save-override"
            >
              {save.isPending ? "Saving…" : "Save override"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
